/**
 * BulkDelegateImport - Import delegates from CSV/Excel files
 * Supports: .csv, .xlsx, .xls
 * Features:
 * - Column mapping
 * - Preview before import
 * - Error reporting
 * - Download error report
 */
import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, Download, FileText, Users, Mail, AlertTriangle,
  CheckCircle, XCircle, Loader2, FileSpreadsheet, Table, Eye
} from 'lucide-react';
import { FIELD_MAPPINGS, TEMPLATE_FIELDS } from './delegates/ImportConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkDelegateImport = ({ eventId, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: upload, 2: map, 3: preview, 4: results
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [parsedData, setParsedData] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [errorRows, setErrorRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(false);
  const [defaultTier, setDefaultTier] = useState('');
  
  // Template customization state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplateFields, setSelectedTemplateFields] = useState(
    TEMPLATE_FIELDS.filter(f => f.required || ['email', 'phone', 'company', 'title', 'meal_access', 'accommodation_access'].includes(f.key)).map(f => f.key)
  );
  
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Get auth token
  const getToken = () => {
    try {
      const authStorage = localStorage.getItem('eventflow-auth');
      if (authStorage) {
        const parsed = JSON.parse(authStorage);
        return parsed?.state?.token || localStorage.getItem('eventflow_token');
      }
    } catch (e) {}
    return localStorage.getItem('eventflow_token');
  };

  const headers_api = {
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  };

  // Fetch tiers for the event
  const { data: tiers = [] } = useQuery({
    queryKey: ['tiers', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/events/${eventId}/tiers`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      return res.ok ? res.json() : [];
    },
    enabled: isOpen
  });

  // Auto-map columns based on header names
  const autoMapColumns = useCallback((fileHeaders) => {
    const mapping = {};
    
    for (const [field, aliases] of Object.entries(FIELD_MAPPINGS)) {
      for (const header of fileHeaders) {
        const normalizedHeader = header.toLowerCase().trim();
        if (aliases.includes(normalizedHeader)) {
          mapping[field] = header;
          break;
        }
      }
    }
    
    return mapping;
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(ext)) {
      toast.error('Unsupported file format. Please use CSV, XLSX, or XLS files.');
      return;
    }

    setFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        let data;
        
        if (ext === '.csv') {
          // Parse CSV
          const text = event.target?.result;
          const workbook = XLSX.read(text, { type: 'string' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        } else {
          // Parse Excel
          const arrayBuffer = event.target?.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        }

        if (data.length < 2) {
          toast.error('File must contain headers and at least one data row');
          return;
        }

        const fileHeaders = data[0].map(h => String(h || '').trim());
        const rows = data.slice(1).filter(row => row.some(cell => cell !== null && cell !== ''));
        
        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map columns
        const autoMapping = autoMapColumns(fileHeaders);
        setColumnMapping(autoMapping);
        
        setStep(2);
        toast.success(`File loaded: ${rows.length} rows found`);
      } catch (err) {
        console.error('File parse error:', err);
        toast.error('Failed to parse file. Please check the format.');
      }
    };

    if (ext === '.csv') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }, [autoMapColumns]);

  // Validate and transform data based on column mapping
  const processData = useCallback(() => {
    const valid = [];
    const errors = [];

    fileData.forEach((row, index) => {
      const rowNum = index + 2; // Account for header row
      const record = {};
      const rowErrors = [];

      // Extract values based on mapping
      for (const [field, headerName] of Object.entries(columnMapping)) {
        // Skip if not mapped (using placeholder value)
        if (headerName === '__not_mapped__' || !headerName) continue;
        
        const colIndex = headers.indexOf(headerName);
        if (colIndex !== -1) {
          record[field] = String(row[colIndex] || '').trim();
        }
      }

      // Validate required fields - only require name (first or last)
      if (!record.first_name && !record.last_name) {
        rowErrors.push(`Missing name (first_name or last_name required)`);
      }

      // Validate email format if provided
      if (record.email && !record.email.includes('@')) {
        rowErrors.push(`Invalid email format`);
      }

      // Check for duplicates in this batch
      // If email exists, check by email; otherwise check by name+phone
      if (record.email) {
        if (valid.some(v => v.email && v.email.toLowerCase() === record.email.toLowerCase())) {
          rowErrors.push(`Duplicate email in file: ${record.email}`);
        }
      } else {
        // Check for duplicate by name + phone combination
        const nameKey = `${(record.first_name || '').toLowerCase()}_${(record.last_name || '').toLowerCase()}_${record.phone || ''}`;
        if (valid.some(v => {
          const vKey = `${(v.first_name || '').toLowerCase()}_${(v.last_name || '').toLowerCase()}_${v.phone || ''}`;
          return vKey === nameKey && !v.email;
        })) {
          rowErrors.push(`Duplicate delegate in file (same name and phone)`);
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNum,
          data: record,
          errors: rowErrors,
          raw: row
        });
      } else {
        // Clean up and normalize data
        record.first_name = record.first_name || '';
        record.last_name = record.last_name || '';
        record.phone = record.phone || '';
        record.province = record.province || '';
        record.district = record.district || '';
        record.company = record.company || '';
        record.title = record.title || '';
        record.gender = record.gender || '';
        // Handle tier - use default tier if not mapped, but ignore __none__ placeholder
        const effectiveTier = defaultTier && defaultTier !== '__none__' ? defaultTier : '';
        record.tier_id = record.tier_id || effectiveTier;
        record.accommodation = record.accommodation || '';
        record.meals = record.meals || '';
        record.payment_status = record.payment_status || '';
        record.badge_id = record.badge_id || '';
        record.qr_code = record.qr_code || '';
        
        valid.push(record);
      }
    });

    setValidRows(valid);
    setErrorRows(errors);
    setStep(3);
  }, [fileData, headers, columnMapping, defaultTier]);

  // Import delegates
  const handleImport = async () => {
    if (validRows.length === 0) {
      toast.error('No valid data to import');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      // Clean up tier value - remove placeholder
      const tierToSend = defaultTier && defaultTier !== '__none__' ? defaultTier : null;
      
      const res = await fetch(`${API_URL}/api/events/${eventId}/delegates/bulk-import-extended`, {
        method: 'POST',
        headers: headers_api,
        body: JSON.stringify({
          delegates: validRows,
          send_welcome_emails: sendWelcomeEmails,
          default_tier_id: tierToSend
        })
      });

      // iter-438k · Never blind-parse — the server can return HTML on
      // 502/504/413 timeouts and the previous res.json() blew up with
      // "Unexpected token '<'". Parse defensively and surface a clear
      // error containing the HTTP status so admins can act on it.
      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      let data;
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const bodyText = await res.text().catch(() => '');
        const snippet = bodyText.slice(0, 200).replace(/\s+/g, ' ').trim();
        const gatewayHint =
          res.status === 413 ? ' — the file is larger than the upload limit. Split it into batches of ~100 rows.' :
          res.status === 502 || res.status === 504 ? ' — the server timed out mid-import. Some rows may have imported; refresh the delegate list and retry the remainder.' :
          res.status === 401 || res.status === 403 ? ' — your session expired. Please sign in again.' :
          '';
        throw new Error(
          `Import failed (${res.status} ${res.statusText})${gatewayHint}${snippet ? ` · server said: ${snippet}` : ''}`
        );
      }

      if (!res.ok) {
        throw new Error(data.detail || data.error || 'Import failed');
      }

      setImportResults(data);
      setStep(4);
      queryClient.invalidateQueries({ queryKey: ['delegates', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      // iter-438k · reconcile every row: successes + skipped + failed
      const totalRows = data.total ?? data.total_rows ?? validRows.length;
      const failed = (data.failed_rows || data.errors || []).length;
      const skipped = data.skipped ?? 0;
      toast.success(
        `Imported ${data.imported} of ${totalRows} · ${skipped} duplicate(s) skipped · ${failed} failed`
      );
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Download error report
  const downloadErrorReport = () => {
    if (errorRows.length === 0) return;

    const errorData = errorRows.map(err => ({
      'Row #': err.row,
      'Errors': err.errors.join('; '),
      ...err.data
    }));

    const ws = XLSX.utils.json_to_sheet(errorData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Errors');
    XLSX.writeFile(wb, 'import_errors.xlsx');
  };

  // Download template
  const downloadTemplate = () => {
    // Build template data with only selected fields
    const buildTemplateRow = (data) => {
      const row = {};
      selectedTemplateFields.forEach(fieldKey => {
        const field = TEMPLATE_FIELDS.find(f => f.key === fieldKey);
        row[fieldKey] = data[fieldKey] || field?.example || '';
      });
      return row;
    };
    
    const sampleData1 = {
      first_name: 'John',
      last_name: 'Doe',
      email: 'john.doe@example.com',
      phone: '+1234567890',
      province: 'Ontario',
      district: 'Toronto',
      company: 'Acme Inc',
      title: 'Manager',
      gender: 'male',
      tier_id: '',
      accommodation: 'yes',
      meals: 'all',
      meal_access: 'yes',
      accommodation_access: 'no',
      session_access: 'yes',
      vip_access: 'no',
      lounge_access: 'no',
      payment_status: 'paid',
      badge_id: 'BADGE001',
      qr_code: ''
    };
    
    const sampleData2 = {
      first_name: 'Jane',
      last_name: 'Smith',
      email: 'jane.smith@example.com',
      phone: '+0987654321',
      province: 'British Columbia',
      district: 'Vancouver',
      company: 'Tech Corp',
      title: 'Developer',
      gender: 'female',
      tier_id: '',
      accommodation: 'no',
      meals: 'lunch_only',
      meal_access: 'yes',
      accommodation_access: 'yes',
      session_access: 'yes',
      vip_access: 'yes',
      lounge_access: 'no',
      payment_status: 'pending',
      badge_id: 'BADGE002',
      qr_code: ''
    };
    
    const templateData = [
      buildTemplateRow(sampleData1),
      buildTemplateRow(sampleData2)
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Delegates');
    XLSX.writeFile(wb, 'delegate_import_template.xlsx');
    toast.success('Template downloaded!');
  };
  
  // Toggle field in template
  const toggleTemplateField = (fieldKey) => {
    const field = TEMPLATE_FIELDS.find(f => f.key === fieldKey);
    if (field?.required) return; // Can't remove required fields
    
    setSelectedTemplateFields(prev => 
      prev.includes(fieldKey)
        ? prev.filter(k => k !== fieldKey)
        : [...prev, fieldKey]
    );
  };

  // Reset state
  const resetState = () => {
    setStep(1);
    setFileData(null);
    setFileName('');
    setHeaders([]);
    setColumnMapping({});
    setParsedData([]);
    setValidRows([]);
    setErrorRows([]);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant="outline" data-testid="bulk-import-delegates-btn">
        <Upload className="h-4 w-4 mr-2" />
        Bulk Import
      </Button>

      <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetState(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Bulk Import Delegates
              <Badge variant="outline" className="ml-2">Step {step} of 4</Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Progress */}
          <div className="mb-4">
            <Progress value={step * 25} className="h-2" />
          </div>

          {/* Step 1: File Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900">Supported Formats:</p>
                      <p className="text-blue-700 mt-1">Excel (.xlsx, .xls) and CSV (.csv) files</p>
                      <p className="font-medium text-blue-900 mt-3">Required Columns:</p>
                      <ul className="mt-1 text-blue-700 space-y-1">
                        <li>• <code className="bg-blue-100 px-1 rounded">first_name</code> or <code className="bg-blue-100 px-1 rounded">last_name</code> (at least one)</li>
                      </ul>
                      <p className="font-medium text-blue-900 mt-3">Optional Columns:</p>
                      <p className="text-blue-700">email, phone, company, title, meal_access, accommodation_access, vip_access, session_access, and more</p>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-blue-600 border-blue-300 hover:bg-blue-100"
                          onClick={() => setShowTemplateDialog(true)}
                        >
                          <Table className="h-3 w-3 mr-1" />
                          Customize Template
                        </Button>
                        <Button 
                          variant="link" 
                          size="sm" 
                          className="p-0 h-auto text-blue-600"
                          onClick={downloadTemplate}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download Template
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload-input"
              />
              
              <div 
                onClick={triggerFileInput}
                className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-10 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                data-testid="file-drop-zone"
              >
                <Upload className="h-12 w-12 text-gray-400 mb-3" />
                <p className="text-lg font-medium text-gray-700">Click to select file</p>
                <p className="text-sm text-gray-500 mt-1">or drag and drop your Excel/CSV file here</p>
                <p className="text-xs text-gray-400 mt-2">Supports .xlsx, .xls, .csv</p>
              </div>
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Map columns from your file</p>
                  <p className="text-sm text-muted-foreground">File: {fileName}</p>
                </div>
                <Badge variant="secondary">{fileData?.length || 0} rows detected</Badge>
              </div>

              <div className="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto p-2">
                {Object.keys(FIELD_MAPPINGS).map((field) => (
                  <div key={field} className="flex items-center gap-2">
                    <Label className="w-32 text-sm capitalize">{field.replace('_', ' ')}:</Label>
                    <Select 
                      value={columnMapping[field] || '__not_mapped__'} 
                      onValueChange={(v) => setColumnMapping({...columnMapping, [field]: v === '__not_mapped__' ? '' : v})}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__not_mapped__">-- Not mapped --</SelectItem>
                        {headers.map((h, i) => (
                          <SelectItem key={i} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Options */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Switch
                    id="send-emails"
                    checked={sendWelcomeEmails}
                    onCheckedChange={setSendWelcomeEmails}
                  />
                  <Label htmlFor="send-emails" className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    Send welcome emails
                  </Label>
                </div>
                {tiers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label>Default Tier:</Label>
                    <Select value={defaultTier || '__none__'} onValueChange={setDefaultTier}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Select tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {tiers.map(tier => (
                          <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={processData}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview Data
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="font-medium">{validRows.length} valid rows</span>
                  </div>
                  {errorRows.length > 0 && (
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="font-medium">{errorRows.length} errors</span>
                    </div>
                  )}
                </div>
                {errorRows.length > 0 && (
                  <Button variant="outline" size="sm" onClick={downloadErrorReport}>
                    <Download className="h-4 w-4 mr-1" />
                    Download Error Report
                  </Button>
                )}
              </div>

              {/* Error Summary */}
              {errorRows.length > 0 && (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-4">
                    <p className="font-medium text-red-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Rows with errors (will be skipped):
                    </p>
                    <ul className="mt-2 text-sm text-red-700 max-h-32 overflow-y-auto">
                      {errorRows.slice(0, 10).map((err, i) => (
                        <li key={i}>Row {err.row}: {err.errors.join(', ')}</li>
                      ))}
                      {errorRows.length > 10 && (
                        <li className="text-red-500 font-medium">...and {errorRows.length - 10} more errors</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Preview Table */}
              <div className="border rounded-lg overflow-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Province</th>
                      <th className="px-3 py-2 text-left">Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validRows.slice(0, 20).map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2">{i + 1}</td>
                        <td className="px-3 py-2">{row.first_name} {row.last_name}</td>
                        <td className="px-3 py-2">{row.email || <span className="text-muted-foreground italic">No email</span>}</td>
                        <td className="px-3 py-2">{row.phone}</td>
                        <td className="px-3 py-2">{row.province}</td>
                        <td className="px-3 py-2">{row.company}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {validRows.length > 20 && (
                  <p className="p-2 text-center text-sm text-muted-foreground bg-gray-50">
                    Showing 20 of {validRows.length} rows
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <Button onClick={handleImport} disabled={importing || validRows.length === 0}>
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      Import {validRows.length} Delegates
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && importResults && (
            <div className="space-y-4">
              <div className="text-center py-6">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold">Import Complete!</h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-slate-700">{importResults.total || (importResults.imported + importResults.skipped + (importResults.failed_rows?.length || 0))}</p>
                    <p className="text-sm text-muted-foreground">Total Processed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{importResults.imported}</p>
                    <p className="text-sm text-muted-foreground">Imported</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-sm text-muted-foreground">Skipped</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-3xl font-bold text-red-600">{importResults.failed_rows?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </CardContent>
                </Card>
              </div>

              {importResults.failed_rows?.length > 0 && (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-4">
                    <p className="font-medium text-red-800">Failed rows:</p>
                    <ul className="mt-2 text-sm text-red-700">
                      {importResults.failed_rows.slice(0, 5).map((row, i) => (
                        <li key={i}>{row.name || row.email}: {row.error}</li>
                      ))}
                    </ul>
                    {importResults.failed_rows.length > 5 && (
                      <p className="text-sm text-red-600 mt-1">...and {importResults.failed_rows.length - 5} more</p>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        const ws = XLSX.utils.json_to_sheet(importResults.failed_rows);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Failed');
                        XLSX.writeFile(wb, 'import_failed_rows.xlsx');
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download Failed Rows
                    </Button>
                  </CardContent>
                </Card>
              )}

              {importResults.skipped_duplicates?.length > 0 && (
                <Card className="bg-yellow-50 border-yellow-200">
                  <CardContent className="pt-4">
                    <p className="font-medium text-yellow-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Skipped Duplicates ({importResults.skipped_duplicates.length}):
                    </p>
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-yellow-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left text-yellow-800">Row</th>
                            <th className="px-2 py-1 text-left text-yellow-800">Name</th>
                            <th className="px-2 py-1 text-left text-yellow-800">Email</th>
                            <th className="px-2 py-1 text-left text-yellow-800">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResults.skipped_duplicates.slice(0, 20).map((dup, i) => (
                            <tr key={i} className="border-t border-yellow-200">
                              <td className="px-2 py-1 text-yellow-700">{dup.row}</td>
                              <td className="px-2 py-1 text-yellow-700">{dup.name}</td>
                              <td className="px-2 py-1 text-yellow-700">{dup.email}</td>
                              <td className="px-2 py-1 text-yellow-700 text-xs">{dup.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {importResults.skipped_duplicates.length > 20 && (
                      <p className="text-sm text-yellow-600 mt-1">...and {importResults.skipped_duplicates.length - 20} more duplicates</p>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => {
                        const ws = XLSX.utils.json_to_sheet(importResults.skipped_duplicates);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, 'Duplicates');
                        XLSX.writeFile(wb, 'import_skipped_duplicates.xlsx');
                      }}
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Download Duplicates List
                    </Button>
                  </CardContent>
                </Card>
              )}

              <DialogFooter>
                <Button onClick={() => { setIsOpen(false); resetState(); }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Customization Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table className="h-5 w-5 text-blue-600" />
              Customize Template Fields
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select which fields to include in your import template. Required fields cannot be removed.
            </p>
            
            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
              {TEMPLATE_FIELDS.map(field => (
                <div
                  key={field.key}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    selectedTemplateFields.includes(field.key)
                      ? 'bg-blue-50 border-blue-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  } ${field.required ? 'opacity-90' : ''}`}
                  onClick={() => toggleTemplateField(field.key)}
                >
                  <input
                    type="checkbox"
                    checked={selectedTemplateFields.includes(field.key)}
                    onChange={() => toggleTemplateField(field.key)}
                    disabled={field.required}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{field.label}</p>
                    {field.required && (
                      <p className="text-xs text-orange-600">Required</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-muted-foreground">
                <strong>Access Control Fields:</strong> Use meal_access, accommodation_access, session_access, vip_access, lounge_access to control delegate permissions.
                <br /><br />
                <strong>Values:</strong> Use "yes", "no", "true", "false", "1", or "0"
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => { downloadTemplate(); setShowTemplateDialog(false); }}>
              <Download className="h-4 w-4 mr-2" />
              Download Template ({selectedTemplateFields.length} fields)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkDelegateImport;
